import React from "react";

const SectionCard = ({ id, title, children }) => (
  <section id={id} className="bg-white rounded-xl shadow-sm p-6 mb-5 scroll-mt-24">
    <h2 className="text-xl font-bold mb-3 text-gray-900">{title}</h2>
    <div className="text-sm text-gray-700 leading-6">{children}</div>
  </section>
);

const FlowStep = ({ label }) => (
  <div className="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 text-center">
    {label}
  </div>
);

export default function UserManualPage() {
  const animateScrollTo = (targetY, duration = 800) => {
    const startY = window.scrollY || window.pageYOffset;
    const distance = targetY - startY;
    const startTime = performance.now();
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);
      window.scrollTo(0, startY + distance * eased);
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  };

  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset) - 12;
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (prefersReducedMotion) {
      window.scrollTo(0, top);
    } else {
      animateScrollTo(top, 850);
    }
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <div className="pt-6 pb-10 max-w-5xl mx-auto px-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">POS System User Manual</h1>
        <p className="text-sm text-gray-600 mt-2">
          Detailed guide in simple terms. Use the Table of Contents to jump to any part.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-bold mb-3 text-gray-900">Table of Contents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <a href="#overview" onClick={scrollToSection("overview")} className="text-blue-700 hover:underline">1. System Overview</a>
          <a href="#roles" onClick={scrollToSection("roles")} className="text-blue-700 hover:underline">2. Who Uses What</a>
          <a href="#admin-flow" onClick={scrollToSection("admin-flow")} className="text-blue-700 hover:underline">3. Admin Daily Flow</a>
          <a href="#cashier-flow" onClick={scrollToSection("cashier-flow")} className="text-blue-700 hover:underline">4. Cashier Daily Flow</a>
          <a href="#inventory-terms" onClick={scrollToSection("inventory-terms")} className="text-blue-700 hover:underline">5. Inventory Terms</a>
          <a href="#modules" onClick={scrollToSection("modules")} className="text-blue-700 hover:underline">6. Modules Guide</a>
          <a href="#flowchart" onClick={scrollToSection("flowchart")} className="text-blue-700 hover:underline">7. Flowchart</a>
          <a href="#tips" onClick={scrollToSection("tips")} className="text-blue-700 hover:underline">8. Tips and Common Issues</a>
        </div>
      </div>

      <SectionCard id="overview" title="1. System Overview">
        <p>
          This POS system manages products, raw materials, and inventory per branch. Admins control master data,
          stock movements, and reports. Cashiers handle sales and day-to-day operational logs.
        </p>
        <p>
          The system separates central warehouse stock (admin) from released branch stock. This helps you track
          where items physically are and who is responsible for them.
        </p>
        <p>
          All transactions, production, disposal, and raw material usage are logged and visible in the Admin logs
          for audit and tracking.
        </p>
      </SectionCard>

      <SectionCard id="roles" title="2. Who Uses What">
        <p><strong>Admin:</strong> Maintains Products, Raw Materials, Inventory, Reports, Transactions, Logs, User List.</p>
        <p><strong>Cashier:</strong> Uses Sales, Products Catalog, Production, Production Logs, Disposal, Raw Material Usage.</p>
        <p>
          Admins can view all branches. Cashiers are restricted to their assigned branch and can only see
          transactions and logs for that branch.
        </p>
      </SectionCard>

      <SectionCard id="admin-flow" title="3. Admin Daily Flow">
        <p><strong>Step 1: Set up products</strong></p>
        <p>1. Go to Products.</p>
        <p>2. Click Add Product.</p>
        <p>3. Enter name, category, unit type, and universal price.</p>
        <p>4. Optional: select a branch and set a branch price override.</p>
        <p>5. Save to create the product and admin stock row.</p>

        <p><strong>Step 2: Set up raw materials</strong></p>
        <p>1. Go to Raw Materials.</p>
        <p>2. Add each raw material with unit type and optional image.</p>
        <p>3. Set initial quantity if needed.</p>

        <p><strong>Step 3: Release stock to branches</strong></p>
        <p>1. Go to Inventory.</p>
        <p>2. Select a branch tab.</p>
        <p>3. Use Release to move stock from admin warehouse to the branch.</p>
        <p>4. Use Adjust when correcting stock quantities.</p>

        <p><strong>Step 4: Monitor operations</strong></p>
        <p>1. Check Transactions for sales and payment records.</p>
        <p>2. Review Production Logs and Waste Logs.</p>
        <p>3. Review Raw Material Logs to verify usage and reductions.</p>
      </SectionCard>

      <SectionCard id="cashier-flow" title="4. Cashier Daily Flow">
        <p><strong>Step 1: Selling products</strong></p>
        <p>1. Open Sales.</p>
        <p>2. Search or browse products.</p>
        <p>3. Add items to cart and adjust quantities.</p>
        <p>4. Collect payment and confirm the order.</p>

        <p><strong>Step 2: Recording production</strong></p>
        <p>1. Open Production.</p>
        <p>2. Select product and quantity produced.</p>
        <p>3. Save to increase branch stock.</p>

        <p><strong>Step 3: Recording disposal</strong></p>
        <p>1. Open Disposal.</p>
        <p>2. Select the wasted product and quantity.</p>
        <p>3. Provide a reason and submit.</p>

        <p><strong>Step 4: Recording raw material usage</strong></p>
        <p>1. Open Raw Material Usage.</p>
        <p>2. Select material and quantity used.</p>
        <p>3. Submit to reduce branch released stock.</p>

        <p><strong>Step 5: Verify logs</strong></p>
        <p>1. Use Production Logs and Disposal Logs to confirm entries.</p>
        <p>2. Use Raw Material Logs to verify material usage.</p>
      </SectionCard>

      <SectionCard id="inventory-terms" title="5. Inventory Terms">
        <p><strong>Stocks Available:</strong> Central admin warehouse stock (not assigned to any branch).</p>
        <p><strong>Stocks Released:</strong> Stock already moved to the selected branch.</p>
        <p><strong>Release:</strong> Move stock from admin warehouse to a branch.</p>
        <p><strong>Adjust:</strong> Manual correction of stock for admin or branch.</p>
        <p><strong>Branch tab:</strong> Controls which branch you are viewing and releasing to.</p>
        <p><strong>Low stock:</strong> Triggered when quantity is below the low stock threshold.</p>
      </SectionCard>

      <SectionCard id="modules" title="6. Modules Guide">
        <p><strong>Products:</strong> Create and manage product details, universal price, and branch overrides.</p>
        <p><strong>Raw Materials:</strong> Create and manage material records used in production.</p>
        <p><strong>Inventory:</strong> View admin stock and released branch stock. Release or adjust quantities.</p>
        <p><strong>Sales:</strong> Cashier sales terminal and transaction creation.</p>
        <p><strong>Production:</strong> Record finished bakery output to increase branch stock.</p>
        <p><strong>Disposal:</strong> Record wasted or expired bakery products.</p>
        <p><strong>Raw Material Usage:</strong> Record material consumption and deduct stock.</p>
        <p><strong>Logs:</strong> Production Logs, Waste Logs, Raw Material Logs, Transaction history.</p>
        <p><strong>Reports:</strong> Sales summary and exports with filters.</p>
      </SectionCard>

      <SectionCard id="flowchart" title="7. Flowchart">
        <div className="space-y-3">
          <FlowStep label="Admin adds Products / Raw Materials" />
          <div className="text-center text-gray-500">-&gt;</div>
          <FlowStep label="Admin releases stock to branch (Inventory)" />
          <div className="text-center text-gray-500">-&gt;</div>
          <FlowStep label="Cashier sells products (Sales)" />
          <div className="text-center text-gray-500">-&gt;</div>
          <FlowStep label="Cashier records production output (Production)" />
          <div className="text-center text-gray-500">-&gt;</div>
          <FlowStep label="Cashier records waste (Disposal) + material consumption (Raw Material Usage)" />
          <div className="text-center text-gray-500">-&gt;</div>
          <FlowStep label="Admin checks logs and reports" />
        </div>
      </SectionCard>

      <SectionCard id="tips" title="8. Tips and Common Issues">
        <p><strong>Branch view:</strong> Always confirm the selected branch before releasing or adjusting stock.</p>
        <p><strong>Low stock alerts:</strong> These trigger when quantity is below the threshold. Restock in Inventory.</p>
        <p><strong>Price overrides:</strong> Use branch pricing when different prices apply per branch.</p>
        <p><strong>Transactions missing:</strong> Check date filters and ensure local time is used.</p>
        <p><strong>Inventory negative:</strong> Release or adjust stock before recording usage or disposal.</p>
        <p><strong>Refresh:</strong> If data looks stale, reload the page after saving changes.</p>
      </SectionCard>
    </div>
  );
}
